import React, { useEffect } from "react";

// Industry sections data
const sections = [
  {
    title: "Automotive",
    content: (
      <>
        <p>
          The automotive industry is a dynamic and ever-evolving sector that constantly pushes the boundaries of innovation. From autonomous driving technologies to electric and hybrid vehicles, this industry is reshaping transportation. At our company, we provide forward-thinking solutions that meet the demands of this rapidly changing landscape.
        </p>
        <p>
          Our commitment to excellence goes beyond industry standards, offering tested and reliable products that excel in demanding conditions. Whether it's enhancing vehicle connectivity or improving fuel efficiency, we are at the forefront of automotive engineering, crafting solutions that anticipate tomorrow's challenges.
        </p>
        <p>
          We understand that reliability and durability are paramount in the automotive industry. Our products are designed to withstand daily use, offering robustness and longevity. Additionally, we prioritize compact and lightweight components to optimize performance and efficiency, making them ideal for integration into modern vehicles.
        </p>
      </>
    ),
    image: "/img/prototype-applications-of-cnc-machining-for-automotive-industry.jpg",
  },
  {
    title: "Food Industry",
    content: (
      <>
        <p>
          The food industry is transforming with advancements in automation, quality control, and sustainability. Our solutions help manufacturers improve efficiency, reduce waste, and ensure the highest food safety standards. From hygienic designs to corrosion-resistant materials, we address the unique challenges of this sector.
        </p>
        <p>
          We offer products that support 24/7 operations, ensuring reliability and efficiency. By leveraging our expertise, food manufacturers can meet strict regulations while optimizing production processes and reducing environmental impact.
        </p>
        <p>
          Our team of experts works closely with clients to develop customized solutions tailored to their specific needs. Whether it's improving overall equipment efficiency or reducing energy consumption, we are committed to driving innovation in the food industry.
        </p>
      </>
    ),
    image: "/img/cookie-factory-171301497-iStock_Sykono.jpg",
  },
  {
    title: "Tyre Industry",
    content: (
      <>
        <p>
          The tyre industry is a cornerstone of the automotive sector, producing high-performance, durable, and sustainable tyres. We support manufacturers in optimizing production processes, from raw material mixing to tyre inspection.
        </p>
        <p>
          Our solutions enhance efficiency and quality, ensuring competitiveness in the market. By partnering with us, tyre manufacturers can achieve optimal results with the best energy efficiency, meeting the demands of modern vehicles.
        </p>
        <p>
          We understand the complexities of tyre manufacturing, including the need for precision and durability. Our products are designed to withstand the rigors of production, ensuring long-lasting performance and reliability.
        </p>
      </>
    ),
    image: "/img/92916dc6b9b54d508c0a9c60aee31a07.jpeg",
  },
  {
    title: "Machine Tool Industry",
    content: (
      <>
        <p>
          The machine tool industry relies on precision and reliability. Our solutions enable manufacturers to produce high-quality tools that meet the demands of modern manufacturing. From automation to energy efficiency, we provide cutting-edge technologies.
        </p>
        <p>
          We understand the importance of durability and performance in this sector. Our products are designed to withstand rigorous use, ensuring long-term reliability and efficiency in production processes.
        </p>
        <p>
          Our team works closely with manufacturers to develop customized solutions that address their unique challenges. Whether it's improving precision or reducing downtime, we are committed to driving innovation in the machine tool industry.
        </p>
      </>
    ),
    image: "/img/b4784db478337d60088f2fef1f33e285.jpg",
  },
  {
    title: "Mining",
    content: (
      <>
        <p>
          The mining industry operates in some of the most challenging environments, requiring robust and reliable equipment. Our solutions are designed to withstand harsh conditions, ensuring safety and efficiency in operations.
        </p>
        <p>
          From material handling to dust control, our products enhance productivity and reduce downtime. We are committed to providing innovative solutions that meet the unique demands of the mining sector.
        </p>
        <p>
          Our expertise in energy-efficient systems helps mining companies reduce operational costs while maintaining high performance. By partnering with us, mining operations can achieve sustainable growth and improved resource extraction.
        </p>
      </>
    ),
    image: "/img/2183365.jpg",
  },
  {
    title: "Oil & Gas",
    content: (
      <>
        <p>
          The oil and gas industry demands solutions that can operate in extreme conditions. Our products are engineered to deliver performance and reliability, even in the harshest environments.
        </p>
        <p>
          From fluid handling to automation, we provide durable and efficient solutions that optimize operations. By partnering with us, companies can enhance productivity and ensure sustainable practices in this critical sector.
        </p>
        <p>
          Our team collaborates closely with industry professionals to develop customized solutions tailored to specific applications. Whether it's improving safety or reducing downtime, we are dedicated to driving innovation in the oil and gas industry.
        </p>
      </>
    ),
    image: "/img/power-plant-gas-oil-industry-twilight_41204-9388.avif",
  },
];

// IndustrySection Component
const IndustrySection = ({ title, content, image, reverse, id }) => {
  return (
    <div id={id} className={`row align-items-center section py-5  ${reverse ? "flex-row-reverse" : ""}`}>
      <div className="col-md-6 text-section">
        <h2>{title}</h2>
        <div>{content}</div>
      </div>
      <div className="col-md-6 image-section">
        <img src={image} alt={title} className="img-fluid" />
      </div>
    </div>
  );
};

// IndustryPage Component
const IndustryPage = () => {
  // Function to scroll to the section based on the hash
  const scrollToSection = () => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  // Scroll to the section on initial load
  useEffect(() => {
    scrollToSection();
  }, []);

  // Listen for hash changes and scroll to the section
  useEffect(() => {
    const handleHashChange = () => {
      scrollToSection();
    };

    // Add event listener for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Manually check for hash changes every 100ms (fallback for React Router)
    const interval = setInterval(() => {
      const currentHash = window.location.hash;
      if (currentHash !== handleHashChange.lastHash) {
        handleHashChange.lastHash = currentHash;
        handleHashChange();
      }
    }, 100);

    // Cleanup
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="container my-5">
      {sections.map((section, index) => (
        <IndustrySection
          key={index}
          id={section.title.toLowerCase().replace(/ /g, "-")}
          title={section.title}
          content={section.content}
          image={section.image}
          reverse={index % 2 !== 0}
        />
      ))}
    </div>
  );
};

export default IndustryPage;